import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import './LegalPage.css';

export default function CookiePolicy() {
  return (
    <>
      <Helmet>
        <title>Cookiepolitik – TopCasinoDansk</title>
        <meta name="description" content="Læs om, hvordan TopCasinoDansk bruger cookies, og hvordan du administrerer dine cookiepræferencer." />
      </Helmet>

      <PageHero
        title="Cookiepolitik"
        subtitle="Sidst opdateret: april 2026"
        breadcrumb={<><Link to="/">Forside</Link> › Cookiepolitik</>}
      />

      <section className="legal-section section">
        <div className="container legal-layout">
          <nav className="legal-toc">
            <div className="legal-toc-inner">
              <h4>Indhold</h4>
              <ol>
                <li><a href="#hvad-er-cookies">1. Hvad er cookies?</a></li>
                <li><a href="#hvilke-cookies">2. Cookies vi bruger</a></li>
                <li><a href="#tredjepartscookies">3. Tredjepartscookies</a></li>
                <li><a href="#administrer">4. Administrer cookies</a></li>
                <li><a href="#samtykke">5. Samtykke</a></li>
                <li><a href="#opdateringer">6. Opdateringer</a></li>
              </ol>
            </div>
          </nav>

          <article className="legal-content">
            <div className="legal-intro">
              <p>
                Denne cookiepolitik forklarer, hvilke cookies TopCasinoDansk.com anvender, hvad de bruges til,
                og hvordan du kan administrere dem. Politikken er en del af vores{' '}
                <Link to="/privatlivspolitik">privatlivspolitik</Link>.
              </p>
            </div>

            <section id="hvad-er-cookies">
              <h2>1. Hvad er cookies?</h2>
              <p>
                Cookies er små tekstfiler, som et websted gemmer på din enhed (computer, tablet eller smartphone),
                når du besøger siden. Cookies bruges til at huske dine præferencer, analysere trafikmønstre og
                understøtte funktionalitet på webstedet.
              </p>
              <p>
                Cookies kan være <strong>session-cookies</strong> (slettes, når du lukker browseren) eller
                <strong> vedvarende cookies</strong> (forbliver på din enhed i en bestemt periode eller indtil du sletter dem).
              </p>
            </section>

            <section id="hvilke-cookies">
              <h2>2. Cookies vi bruger</h2>

              <h3>2.1 Nødvendige cookies</h3>
              <p>Disse cookies er nødvendige for, at webstedet fungerer korrekt. De kan ikke deaktiveres.</p>
              <div className="legal-table-wrap">
                <table className="legal-table">
                  <thead>
                    <tr><th>Cookie-navn</th><th>Formål</th><th>Varighed</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>tcd_age_confirmed</td><td>Gemmer brugerens aldersbekræftelse</td><td>Sessionsbaseret (localStorage)</td></tr>
                    <tr><td>tcd_cookies_accepted</td><td>Gemmer brugerens cookiesamtykke</td><td>1 år (localStorage)</td></tr>
                  </tbody>
                </table>
              </div>

              <h3>2.2 Analytiske cookies</h3>
              <p>
                Vi anvender Google Analytics til at forstå, hvordan besøgende bruger vores websted.
                Disse cookies indsamler anonymiserede oplysninger om sidevisninger, varighed og trafikkilde.
              </p>
              <div className="legal-table-wrap">
                <table className="legal-table">
                  <thead>
                    <tr><th>Cookie-navn</th><th>Formål</th><th>Varighed</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>_ga</td><td>Skelner mellem unikke brugere</td><td>2 år</td></tr>
                    <tr><td>_ga_*</td><td>Gemmer session-tilstand</td><td>2 år</td></tr>
                    <tr><td>_gid</td><td>Skelner mellem brugere</td><td>24 timer</td></tr>
                    <tr><td>_gat</td><td>Begrænser anmodningsfrekvens</td><td>1 minut</td></tr>
                  </tbody>
                </table>
              </div>

              <h3>2.3 Marketing- og sporingscookies</h3>
              <p>
                Vores affiliate-links kan sætte sporingscookies på din enhed via tredjepartsnetværk, når du klikker
                på et link til et casino. Disse cookies registrerer, om du efterfølgende opretter en konto, så vi
                kan modtage kommission.
              </p>
            </section>

            <section id="tredjepartscookies">
              <h2>3. Tredjepartscookies</h2>
              <p>Vi kan integrere indhold og tjenester fra følgende tredjeparter, som kan sætte egne cookies:</p>
              <ul className="legal-list">
                <li><strong>Google Analytics</strong> – trafikanalyse og statistik</li>
                <li><strong>Affiliate-netværker</strong> – kommissionssporing via casino-links</li>
              </ul>
              <p>
                Vi har ingen kontrol over tredjeparters cookies. For information om deres cookiepraksis,
                bedes du konsultere de respektive tredjeparters privatlivspolitikker.
              </p>
            </section>

            <section id="administrer">
              <h2>4. Administrer og slet cookies</h2>
              <p>Du kan til enhver tid administrere eller slette cookies via din browsers indstillinger:</p>
              <ul className="legal-list">
                <li><strong>Google Chrome:</strong> Indstillinger → Beskyttelse af personlige oplysninger → Cookies</li>
                <li><strong>Mozilla Firefox:</strong> Indstillinger → Beskyttelse af personlige oplysninger → Cookies og webstedsdata</li>
                <li><strong>Safari:</strong> Indstillinger → Beskyttelse af personlige oplysninger → Administrer webstedsdata</li>
                <li><strong>Microsoft Edge:</strong> Indstillinger → Cookies og webstedstilladelser</li>
              </ul>
              <p>
                Bemærk, at deaktivering af visse cookies kan påvirke funktionaliteten af vores websted og din brugeroplevelse.
              </p>
              <p>
                Du kan fravælge Google Analytics via{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out Add-on
                </a>.
              </p>
            </section>

            <section id="samtykke">
              <h2>5. Samtykke til cookies</h2>
              <p>
                Når du besøger TopCasinoDansk.com for første gang, vises en cookiebanner, der informerer dig om
                vores cookiebrug og beder om dit samtykke til analytiske og markedsføringscookies. Nødvendige cookies
                sættes uden samtykke, da de er påkrævet for webstedets funktionalitet.
              </p>
              <p>
                Du kan til enhver tid tilbagekalde dit samtykke ved at rydde cookies i din browser og afvise
                cookiebanneren, næste gang du besøger vores websted.
              </p>
            </section>

            <section id="opdateringer">
              <h2>6. Opdateringer til denne politik</h2>
              <p>
                Vi forbeholder os retten til at opdatere denne cookiepolitik løbende for at afspejle ændringer
                i vores cookiepraksis eller lovgivningsmæssige krav. Opdateringsdatoen øverst på siden afspejler
                den seneste version. Ved væsentlige ændringer vil vi informere dig via en synlig meddelelse på vores websted.
              </p>
              <p>
                Har du spørgsmål om vores cookiebrug, er du velkommen til at kontakte os på{' '}
                <Link to="/kontakt">vores kontaktside</Link> eller via e-mail: privacy@topcasinodansk.com
              </p>
            </section>
          </article>
        </div>
      </section>
    </>
  );
}
